import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import messageService from '../../services/messages';
import VideoCall from './VideoCall';
import webrtcService from '../../services/webrtc';
import { 
  FaSearch, FaPaperPlane, FaImage, FaSmile, 
  FaArrowLeft, FaEllipsisV, FaCheck, FaCheckDouble,
  FaSpinner, FaTimes, FaComments, FaUserPlus,
  FaPencilAlt, FaPlay, FaVideo, FaPhone, FaPhoneSlash
} from 'react-icons/fa';
import Loader from '../common/Loader';
import Boxview from '../common/Boxview';
import { useBoxview } from '../../hooks/useBoxview';
import './messages.css';
import EmojiPicker from 'emoji-picker-react';
import toast from 'react-hot-toast';

const Messages = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const [lastMessageId, setLastMessageId] = useState(0);
  const [messageIds, setMessageIds] = useState(new Set());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [incomingCall, setIncomingCall] = useState(false);
  const [pendingCall, setPendingCall] = useState(null); // { fromUserId, offer }

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const chatRefreshInterval = useRef(null);
  const conversationsRefreshInterval = useRef(null);
  const [peerReady, setPeerReady] = useState(false);
  const [myPeerId, setMyPeerId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const boxview = useBoxview();

  const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL || 'https://murima.julesferry-ga.com/uploads/';

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

// Dans Messages.jsx


useEffect(() => {
  if (!user?.id) return;

  webrtcService.init(user.id, {
    onCallReceived: (call) => {
      setPendingCall(call);
      setIncomingCall(true);
    },
    onRemoteStream: (stream) => {
      // Géré par VideoCall
    }
  });

  // Écouter l’événement 'open' via une méthode dédiée
  const checkPeerInterval = setInterval(() => {
    const id = webrtcService.getPeerId();
    if (id && !peerReady) {
      setPeerReady(true);
      setMyPeerId(id);
      console.log('✅ PeerJS prêt, ID:', id);
      clearInterval(checkPeerInterval);
    }
  }, 500);

  return () => clearInterval(checkPeerInterval);
}, [user?.id]);
// Démarrer un appel
const startVideoCall = async () => {
  if (!selectedChat) return;

  if (!peerReady) {
    toast.error('Connexion en cours, veuillez patienter...');
    return;
  }

  console.log("🔍 startVideoCall pour", selectedChat.user_id);
  const response = await api.get('/get-peer-id.php', {
    params: { user_id: selectedChat.user_id }
  });
  console.log("📡 Réponse get-peer-id:", response.data);

  if (!response.data.success || !response.data.peer_id) {
    toast.error(`${selectedChat.pseudo} n'est pas disponible`);
    return;
  }

  const targetPeerId = response.data.peer_id;
  console.log("🎯 Peer ID cible:", targetPeerId);
  console.log("🆔 Mon Peer ID:", myPeerId);

  try {
    await webrtcService.startCall(targetPeerId);
    setShowVideoCall(true);
  } catch (err) {
    console.error("❌ Erreur startCall:", err);
    toast.error(err.message || 'Impossible de démarrer l\'appel');
  }
};
// Accepter un appel
const acceptIncomingCall = async () => {
  if (!pendingCall) return;
  await webrtcService.answerCall(pendingCall);
  setShowVideoCall(true);
  setIncomingCall(false);
  setPendingCall(null);
};

// Refuser un appel
const rejectIncomingCall = () => {
  if (pendingCall) pendingCall.close();
  setIncomingCall(false);
  setPendingCall(null);
  toast('Appel refusé');
};

  // --- Reste du code inchangé (conversations, messages, etc.) ---
  const openImageBoxview = (url, caption = '') => {
    const imageList = [{ url, caption }];
    boxview.openBoxview(imageList, 0);
  };

  const openAvatarBoxview = (e, avatarUrl, pseudo) => {
    e.stopPropagation();
    openImageBoxview(avatarUrl, `Avatar de ${pseudo}`);
  };

  const openMediaBoxview = (e, mediaUrl, type, sender) => {
    e.stopPropagation();
    const caption = type === 'image' ? `Photo de ${sender}` : `Vidéo de ${sender}`;
    openImageBoxview(mediaUrl, caption);
  };

  const loadConversations = useCallback(async () => {
    if (!user?.id) return [];
    
    try {
      const response = await api.get('/get-conversations.php', {
        params: { user_id: user.id }
      });
      
      if (response.data.success) {
        const formattedConversations = response.data.conversations.map(conv => ({
          user_id: conv.other_user_id,
          pseudo: conv.pseudo,
          avatar: conv.avatar ? `${UPLOADS_URL}/${conv.avatar}` : null,
          en_ligne: Boolean(conv.en_ligne),
          is_verified: Boolean(conv.is_verified),
          is_premium: Boolean(conv.is_premium),
          last_message: conv.last_message || 'Cliquez pour démarrer une conversation',
          last_message_time: conv.last_message_time || '',
          unread_count: conv.unread_count || 0,
          isTyping: Boolean(conv.isTyping)
        }));
        
        setConversations(formattedConversations);
        return formattedConversations;
      }
    } catch (error) {
      console.error('Erreur chargement conversations:', error);
    }
    return [];
  }, [user?.id, UPLOADS_URL]);

  const loadMessages = useCallback(async (userId) => {
    if (!user?.id || !userId) return;

    try {
      const response = await api.get('/get-messages.php', {
        params: {
          current_user_id: user.id,
          user_id: userId
        }
      });
      
      if (response.data.success) {
        const formattedMessages = response.data.messages.map(msg => ({
          id: msg.id,
          sender_id: msg.sender_id,
          receiver_id: msg.receiver_id,
          contenu: msg.contenu,
          date_envoi: msg.date_envoi,
          type_media: msg.type_media || 'text',
          media_url: msg.media_url,
          lu: Boolean(msg.lu),
          temp: false
        }));
        
        setMessages(formattedMessages);
        
        if (formattedMessages.length > 0) {
          const lastId = Math.max(...formattedMessages.map(m => m.id));
          setLastMessageId(lastId);
          setMessageIds(new Set(formattedMessages.map(m => m.id)));
        } else {
          setLastMessageId(0);
          setMessageIds(new Set());
        }
      }
    } catch (error) {
      console.error('Erreur chargement messages:', error);
      toast.error('Erreur lors du chargement des messages');
    }
  }, [user?.id]);

  const checkNewMessages = useCallback(async () => {
    if (!selectedChat || isRefreshing || !user?.id) return;
    
    setIsRefreshing(true);
    
    try {
      const params = new URLSearchParams();
      params.append('current_user_id', user.id.toString());
      params.append('user_id', selectedChat.user_id.toString());
      params.append('last_id', lastMessageId.toString());
      
      const response = await api.post('/get-last-messages.php', params);
      
      if (response.data.success && response.data.messages?.length > 0) {
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMessages = response.data.messages
            .filter(msg => !existingIds.has(msg.id))
            .map(msg => ({
              id: msg.id,
              sender_id: msg.sender_id,
              receiver_id: msg.receiver_id,
              contenu: msg.contenu,
              date_envoi: msg.date_envoi,
              type_media: msg.type_media || 'text',
              media_url: msg.media_url,
              lu: Boolean(msg.lu),
              temp: false
            }));
          
          if (newMessages.length === 0) return prev;
          
          return [...prev, ...newMessages].sort(
            (a, b) => new Date(a.date_envoi) - new Date(b.date_envoi)
          );
        });
        
        setMessageIds(prev => {
          const newIds = new Set(prev);
          response.data.messages.forEach(msg => newIds.add(msg.id));
          return newIds;
        });
        
        const lastId = Math.max(...response.data.messages.map(m => m.id));
        if (lastId > lastMessageId) {
          setLastMessageId(lastId);
        }
        
        const chatMessages = document.getElementById('chatMessages');
        if (chatMessages) {
          const isAtBottom = chatMessages.scrollHeight - chatMessages.scrollTop <= chatMessages.clientHeight + 100;
          if (isAtBottom) {
            setTimeout(scrollToBottom, 100);
          }
        }
        
        loadConversations();
      }
      
      const typingResponse = await messageService.checkTyping(user.id, selectedChat.user_id);
      setOtherTyping(typingResponse.is_typing || false);
      
    } catch (error) {
      console.error('❌ Erreur checkNewMessages:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [selectedChat, user?.id, lastMessageId, isRefreshing, loadConversations, scrollToBottom]);

  useEffect(() => {
    if (!user?.id) return;
    
    loadConversations();
    
    conversationsRefreshInterval.current = setInterval(() => {
      loadConversations();
    }, 5000);
    
    return () => {
      if (conversationsRefreshInterval.current) {
        clearInterval(conversationsRefreshInterval.current);
      }
    };
  }, [user?.id, loadConversations]);

  const loadUserProfile = async (userId) => {
    try {
      const response = await api.get(`/get-profile.php`, {
        params: { 
          id: userId, 
          current_user_id: user?.id 
        }
      });
      
      if (response.data.success) {
        const newConv = {
          user_id: userId,
          pseudo: response.data.user.pseudo,
          avatar: response.data.user.avatar ? `${UPLOADS_URL}/${response.data.user.avatar}` : null,
          en_ligne: Boolean(response.data.user.en_ligne),
          is_verified: Boolean(response.data.user.is_verified),
          is_premium: Boolean(response.data.user.is_premium),
          last_message: 'Cliquez pour démarrer une conversation',
          last_message_time: '',
          unread_count: 0,
          isTyping: false
        };
        
        setSelectedChat(newConv);
        setMessages([]);
        setLastMessageId(0);
        setMessageIds(new Set());
        
        navigate(`/messages?user=${userId}`, { replace: true });
      }
    } catch (error) {
      console.error('Erreur chargement profil:', error);
      toast.error('Impossible de charger le profil');
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (selectedChat) {
      chatRefreshInterval.current = setInterval(checkNewMessages, 3000);
      return () => {
        if (chatRefreshInterval.current) {
          clearInterval(chatRefreshInterval.current);
        }
      };
    }
  }, [selectedChat, checkNewMessages]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const userIdFromUrl = params.get('user');
    
    const initMessages = async () => {
      if (!user?.id) return;
      
      setLoading(true);
      
      const convs = await loadConversations();
      
      if (userIdFromUrl) {
        const userIdNum = parseInt(userIdFromUrl);
        const existingConv = convs.find(c => c.user_id === userIdNum);
        
        if (existingConv) {
          selectConversation(existingConv);
        } else {
          await loadUserProfile(userIdNum);
        }
      }
      
      setLoading(false);
    };
    
    initMessages();
  }, [location.search, user?.id]);

  useEffect(() => {
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const selectConversation = (conversation) => {
    setSelectedChat(conversation);
    loadMessages(conversation.user_id);
    
    navigate(`/messages?user=${conversation.user_id}`, { replace: true });
    
    setConversations(prev =>
      prev.map(c =>
        c.user_id === conversation.user_id
          ? { ...c, unread_count: 0 }
          : c
      )
    );
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChat || sending || !user?.id) return;

    setSending(true);
    const messageContent = newMessage.trim();
    setNewMessage('');
    setShowEmojiPicker(false);

    const tempId = 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    
    const tempMessage = {
      id: tempId,
      sender_id: user.id,
      receiver_id: selectedChat.user_id,
      contenu: messageContent,
      date_envoi: new Date().toISOString(),
      type_media: 'text',
      sending: true,
      temp: true
    };
    
    setMessages(prev => [...prev, tempMessage]);
    scrollToBottom();

    try {
      const params = new URLSearchParams();
      params.append('sender_id', user.id.toString());
      params.append('receiver_id', selectedChat.user_id.toString());
      params.append('message', messageContent);
      
      const response = await api.post('/send-message.php', params);
      
      if (response.data.success) {
        setMessages(prev =>
          prev.map(m =>
            m.id === tempId
              ? { 
                  ...m, 
                  id: response.data.message_id,
                  sending: false,
                  temp: false
                }
              : m
          )
        );
        
        await loadConversations();
        
        setConversations(prev => {
          const exists = prev.some(c => c.user_id === selectedChat.user_id);
          
          if (!exists) {
            const newConv = {
              user_id: selectedChat.user_id,
              pseudo: selectedChat.pseudo,
              avatar: selectedChat.avatar,
              en_ligne: selectedChat.en_ligne,
              is_verified: selectedChat.is_verified || false,
              is_premium: selectedChat.is_premium || false,
              last_message: messageContent,
              last_message_time: new Date().toLocaleString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit'
              }),
              unread_count: 0,
              isTyping: false
            };
            return [newConv, ...prev];
          }
          
          return prev;
        });
      } else {
        throw new Error(response.data.error);
      }
    } catch (error) {
      console.error('❌ Erreur envoi:', error);
      toast.error('Erreur lors de l\'envoi');
      setMessages(prev => prev.filter(m => m.id !== tempId));
    } finally {
      setSending(false);
    }
  };

  const handleTyping = () => {
    if (!selectedChat || !user?.id) return;
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    if (!isTyping) {
      setIsTyping(true);
      messageService.setTyping(user.id, selectedChat.user_id, true)
        .catch(err => console.error('Erreur typing:', err));
    }
    
    typingTimeoutRef.current = setTimeout(() => {
      if (isTyping) {
        setIsTyping(false);
        messageService.setTyping(user.id, selectedChat.user_id, false)
          .catch(err => console.error('Erreur stop typing:', err));
      }
    }, 2000);
  };

  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      toast.error('Le fichier doit être une image ou une vidéo');
      e.target.value = '';
      return;
    }
    
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    
    if (file.size > maxSize) {
      toast.error(`Fichier trop volumineux (max ${isVideo ? '50Mo' : '10Mo'})`);
      e.target.value = '';
      return;
    }
    
    const allowedVideoTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo'];
    const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    
    if (isVideo && !allowedVideoTypes.includes(file.type)) {
      toast.error('Format vidéo non supporté. Utilisez MP4, MOV ou AVI');
      e.target.value = '';
      return;
    }
    
    if (isImage && !allowedImageTypes.includes(file.type)) {
      toast.error('Format image non supporté. Utilisez JPG, PNG, GIF ou WEBP');
      e.target.value = '';
      return;
    }
    
    const previewUrl = URL.createObjectURL(file);
    
    setPreviewMedia({
      file,
      url: previewUrl,
      type: isImage ? 'image' : 'video',
      name: file.name,
      size: file.size,
      lastModified: file.lastModified
    });
    
    toast.success(`${isImage ? 'Image' : 'Vidéo'} sélectionnée : ${file.name}`);
    e.target.value = '';
  };

  const confirmSendMedia = async () => {
    if (!previewMedia || !selectedChat || !user?.id) {
      toast.error('Informations manquantes pour l\'envoi');
      return;
    }

    setSending(true);
    const tempId = 'media_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

    const isImage = previewMedia.type === 'image';
    const isVideo = previewMedia.type === 'video';
    
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (previewMedia.file.size > maxSize) {
      toast.error(`Fichier trop volumineux (max ${isVideo ? '50Mo' : '10Mo'})`);
      setSending(false);
      return;
    }

    const tempMediaMessage = {
      id: tempId,
      sender_id: user.id,
      receiver_id: selectedChat.user_id,
      contenu: '[Media]',
      date_envoi: new Date().toISOString(),
      type_media: previewMedia.type,
      media_url: previewMedia.url,
      sending: true,
      temp: true
    };
    
    setMessages(prev => [...prev, tempMediaMessage]);
    scrollToBottom();

    try {
      const formData = new FormData();
      formData.append('file', previewMedia.file);
      formData.append('sender_id', user.id);
      formData.append('receiver_id', selectedChat.user_id);
      formData.append('user_id', user.id);

      const toastId = toast.loading('Envoi en cours...');

      const response = await api.post('/upload-chat-media.php', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'X-User-ID': user.id
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload: ${percentCompleted}%`);
        }
      });

      toast.dismiss(toastId);

      if (response.data.success) {
        setMessages(prev =>
          prev.map(m =>
            m.id === tempId
              ? {
                  id: response.data.message_id,
                  sender_id: user.id,
                  receiver_id: selectedChat.user_id,
                  contenu: response.data.media_type === 'image' ? '📷 Photo' : '🎥 Vidéo',
                  date_envoi: new Date().toISOString(),
                  type_media: response.data.media_type,
                  media_url: response.data.filename,
                  lu: false,
                  temp: false,
                  sending: false
                }
              : m
          )
        );
        
        const mediaText = response.data.media_type === 'image' ? '📷 Photo' : '🎥 Vidéo';
        const timeStr = new Date().toLocaleString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit'
        });
        
        setConversations(prev => {
          const updated = [...prev];
          const convIndex = updated.findIndex(c => c.user_id === selectedChat.user_id);
          
          const updatedConv = {
            user_id: selectedChat.user_id,
            pseudo: selectedChat.pseudo,
            avatar: selectedChat.avatar,
            en_ligne: selectedChat.en_ligne,
            is_verified: selectedChat.is_verified || false,
            is_premium: selectedChat.is_premium || false,
            last_message: mediaText,
            last_message_time: timeStr,
            unread_count: 0,
            isTyping: false
          };
          
          if (convIndex !== -1) {
            updated[convIndex] = updatedConv;
            const [conv] = updated.splice(convIndex, 1);
            updated.unshift(conv);
          } else {
            updated.unshift(updatedConv);
          }
          
          return updated;
        });
        
        toast.success('Média envoyé avec succès !', {
          icon: response.data.media_type === 'image' ? '📷' : '🎥'
        });
        
        cancelMedia();
        
        if (selectedChat) {
          loadMessages(selectedChat.user_id);
        }
        
      } else {
        throw new Error(response.data.error || 'Erreur lors de l\'upload');
      }
      
    } catch (error) {
      console.error('❌ Erreur upload média:', error);
      setMessages(prev => prev.filter(m => m.id !== tempId));
      
      if (error.response?.status === 401) {
        toast.error('Session expirée, veuillez vous reconnecter');
      } else if (error.response?.status === 413) {
        toast.error('Fichier trop volumineux pour le serveur');
      } else if (error.response?.status === 415) {
        toast.error('Format de fichier non supporté par le serveur');
      } else {
        toast.error(error.message || 'Erreur lors de l\'envoi du média');
      }
      
    } finally {
      setSending(false);
    }
  };

  const cancelMedia = useCallback(() => {
    if (previewMedia?.url) {
      URL.revokeObjectURL(previewMedia.url);
    }
    setPreviewMedia(null);
  }, [previewMedia]);

  const handleEmojiClick = (emojiData) => {
    setNewMessage(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60));
    
    if (diff < 1) return 'À l\'instant';
    if (diff < 60) return `Il y a ${diff} min`;
    if (diff < 120) return 'Il y a 1h';
    if (diff < 1440) return `Il y a ${Math.floor(diff / 60)}h`;
    return date.toLocaleDateString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit'
    });
  };

  const getAvatarUrl = (avatar) => {
    if (!avatar) return 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e';
    if (avatar.startsWith('http')) return avatar;
    return `${UPLOADS_URL}/${avatar}`;
  };

  const filteredConversations = conversations.filter(conv =>
    conv.pseudo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const goBack = () => {
    setSelectedChat(null);
    navigate('/messages', { replace: true });
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="messages-page">
      <div className={`conversations-sidebar ${selectedChat && window.innerWidth <= 768 ? 'hidden' : ''}`}>
        <div className="conversations-header">
          <h2>Messages</h2>
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="conversations-list">
          {filteredConversations.length > 0 ? (
            filteredConversations.map(conv => (
              <div
                key={conv.user_id}
                className={`conversation-item ${selectedChat?.user_id === conv.user_id ? 'active' : ''}`}
                onClick={() => selectConversation(conv)}
              >
                <div className="conversation-avatar" onClick={(e) => openAvatarBoxview(e, getAvatarUrl(conv.avatar), conv.pseudo)}>
                  <img src={getAvatarUrl(conv.avatar)} alt={conv.pseudo} />
                  {conv.en_ligne && <span className="online-badge"></span>}
                  {conv.isTyping && (
                    <span className="typing-badge" title="Est en train d'écrire">
                      <FaPencilAlt />
                    </span>
                  )}
                </div>
                <div className="conversation-info">
                  <h4>
                    {conv.pseudo}
                    {conv.isTyping && <span className="typing-text"> écrit...</span>}
                  </h4>
                  <p className="last-message">
                    {conv.isTyping ? (
                      <span className="typing-indicator-message">
                        <FaSmile className="typing-icon" /> Écrit un message...
                      </span>
                    ) : (
                      conv.last_message
                    )}
                  </p>
                </div>
                <div className="conversation-meta">
                  <span className="time">{conv.last_message_time}</span>
                  {conv.unread_count > 0 && !conv.isTyping && (
                    <span className="unread-badge">{conv.unread_count}</span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="no-conversations">
              <FaComments className="no-conversations-icon" />
              <h3>Pas encore de messages</h3>
              <p>Commencez une conversation en découvrant de nouveaux profils</p>
              <button 
                className="no-conversations-btn"
                onClick={() => navigate('/discover')}
              >
                <FaUserPlus /> Découvrir des profils
              </button>
            </div>
          )}
        </div>
      </div>

      <div className={`chat-area ${selectedChat ? 'active' : ''}`}>
        {selectedChat ? (
          <>
            <div className="chat-header">
              <button className="mobile-back" onClick={goBack}>
                <FaArrowLeft />
              </button>
              <div className="chat-user" onClick={() => navigate(`/profile/${selectedChat.user_id}`)}>
                <img src={getAvatarUrl(selectedChat.avatar)} alt={selectedChat.pseudo} />
                <div className="chat-user-info">
                  <h3>{selectedChat.pseudo}</h3>
                  <span className={`status ${selectedChat.en_ligne ? 'online' : 'offline'}`}>
                    {selectedChat.en_ligne ? 'En ligne' : 'Hors ligne'}
                  </span>
                  {otherTyping && (
                    <span className="typing-indicator-small">
                      <FaSmile /> écrit...
                    </span>
                  )}
                </div>
              </div>
              <button 
                className="chat-video-btn"
                onClick={startVideoCall}
                title="Appel vidéo"
                style={{
                  position: 'relative',
                  width: '42px',
                  height: '42px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #ff4b6e, #ff8a5c)',
                  border: 'none',
                  color: 'white',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 12px rgba(255, 75, 110, 0.3)',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 75, 110, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 75, 110, 0.3)';
                }}
              >
                <span style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '0',
                  height: '0',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.3)',
                  transform: 'translate(-50%, -50%)',
                  transition: 'width 0.5s, height 0.5s',
                  pointerEvents: 'none'
                }} className="ripple" />
                <FaVideo />
              </button>
              <button className="chat-options">
                <FaEllipsisV />
              </button>
            </div>

            {incomingCall && selectedChat && (
              <div className="incoming-call-modal">
                <div className="incoming-call-content">
                  <img src={selectedChat.avatar} alt={selectedChat.pseudo} />
                  <h3>{selectedChat.pseudo}</h3>
                  <p>Appel vidéo entrant...</p>
                  <div className="incoming-call-actions">
                    <button onClick={acceptIncomingCall} className="accept-call" title="Accepter">
                      <FaPhone />
                    </button>
                    <button onClick={rejectIncomingCall} className="reject-call" title="Refuser">
                      <FaPhoneSlash />
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="chat-messages" id="chatMessages">
              {messages.map((message, index) => {
                const isOwn = message.sender_id === user?.id;
                const showAvatar = index === 0 || messages[index - 1]?.sender_id !== message.sender_id;

                return (
                  <div
                    key={message.id}
                    className={`message ${isOwn ? 'sent' : 'received'}`}
                  >
                    {!isOwn && showAvatar && (
                      <div className="message-avatar">
                        <img src={getAvatarUrl(selectedChat.avatar)} alt={selectedChat.pseudo} />
                      </div>
                    )}
                    <div className="message-content">
                      {message.type_media === 'text' ? (
                        <div className={`message-bubble ${message.sending ? 'sending' : ''}`}>
                          <p>{message.contenu}</p>
                          <span className="message-time">
                            {formatMessageTime(message.date_envoi)}
                            {isOwn && (
                              <span className="message-status">
                                {message.sending ? (
                                  <FaSpinner className="status-icon sending" />
                                ) : message.lu ? (
                                  <FaCheckDouble className="status-icon read" />
                                ) : (
                                  <FaCheck className="status-icon delivered" />
                                )}
                              </span>
                            )}
                          </span>
                        </div>
                      ) : message.type_media === 'image' ? (
                        <div 
                          className="message-media" 
                          onClick={(e) => openMediaBoxview(
                            e, 
                            `${UPLOADS_URL}/${message.media_url}`, 
                            'image', 
                            isOwn ? 'Vous' : selectedChat.pseudo
                          )}
                        >
                          <img 
                            src={`${UPLOADS_URL}/${message.media_url}`} 
                            alt="Media" 
                            onError={(e) => {
                              e.target.src = 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e';
                            }}
                          />
                          <span className="message-time">{formatMessageTime(message.date_envoi)}</span>
                        </div>
                      ) : message.type_media === 'video' ? (
                        <div 
                          className={`message-media video-container ${isOwn ? 'own-video' : ''}`}
                          onClick={(e) => openMediaBoxview(
                            e, 
                            `${UPLOADS_URL}/${message.media_url}`, 
                            'video', 
                            isOwn ? 'Vous' : selectedChat.pseudo
                          )}
                        >
                          <div className="video-thumbnail-wrapper">
                            <video 
                              src={`${UPLOADS_URL}/${message.media_url}`}
                              preload="metadata"
                              muted
                              disablePictureInPicture
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            >
                              <source src={`${UPLOADS_URL}/${message.media_url}#t=0.1`} type="video/mp4" />
                            </video>
                            
                            <div className="video-badge">
                              <FaPlay className="video-play-icon-small" />
                              <span className="video-duration">VIDÉO</span>
                            </div>
                            
                            <div className="video-play-overlay-hover">
                              <FaPlay className="video-play-icon-hover" />
                              <span>Cliquer pour lire</span>
                            </div>
                          </div>
                          
                          <div className="video-info-bar">
                            <FaPlay className="video-icon" />
                            <span className="video-label">Vidéo</span>
                            {message.lu && isOwn && (
                              <FaCheckDouble className="status-icon-read" />
                            )}
                          </div>
                          
                          <span className="message-time">{formatMessageTime(message.date_envoi)}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
              {otherTyping && (
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-input-area">
              {showEmojiPicker && (
                <div className="emoji-picker">
                  <EmojiPicker onEmojiClick={handleEmojiClick} />
                  <button className="close-emoji" onClick={() => setShowEmojiPicker(false)}>
                    <FaTimes />
                  </button>
                </div>
              )}
              
              {previewMedia && (
                <div className="media-preview">
                  <div className="preview-content">
                    {previewMedia.type === 'image' ? (
                      <img src={previewMedia.url} alt="Preview" />
                    ) : (
                      <video src={previewMedia.url} controls />
                    )}
                  </div>
                  <div className="preview-actions">
                    <button 
                      onClick={confirmSendMedia} 
                      className="btn-confirm"
                      disabled={sending}
                    >
                      {sending ? <FaSpinner className="spinner" /> : 'Envoyer'}
                    </button>
                    <button 
                      onClick={cancelMedia} 
                      className="btn-cancel"
                      disabled={sending}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              <button 
                className="emoji-btn" 
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                disabled={sending}
              >
                <FaSmile />
              </button>
              <button 
                className="media-btn" 
                onClick={handleFileSelect}
                disabled={sending}
              >
                <FaImage />
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*,video/*"
                style={{ display: 'none' }}
              />
              <input
                type="text"
                placeholder="Écrivez votre message..."
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  handleTyping();
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                disabled={sending}
              />
              <button
                className="send-btn"
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sending}
              >
                {sending ? <FaSpinner className="spinner" /> : <FaPaperPlane />}
              </button>
            </div>
          </>
        ) : (
          <div className="no-chat-selected">
            <div className="no-chat-content">
              <FaPaperPlane className="no-chat-icon" />
              <h3>Vos messages</h3>
              <p>Sélectionnez une conversation pour commencer à discuter</p>
            </div>
          </div>
        )}
      </div>

      {showVideoCall && selectedChat && (
        <VideoCall
          onClose={() => {
            setShowVideoCall(false);
            setIncomingCall(false);
            setPendingCall(null);
            webrtcService.endCall(); // assure la fin propre
          }}
          targetName={selectedChat.pseudo}
          targetAvatar={selectedChat.avatar}
        />
      )}
      
      <Boxview 
        isOpen={boxview.isOpen}
        onClose={boxview.closeBoxview}
        images={boxview.images}
        initialIndex={boxview.currentIndex}
      />
    </div>
  );
};

export default Messages;